/** @jsxImportSource @emotion/react */
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { memo } from 'react'
import Text from '../ts-components/Text'
import { css } from '@emotion/react'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  MailOutlined,
  StopOutlined,
} from '@ant-design/icons'

import type { InvitationDeliveryStatus } from '@sportninja/common/features/competition/operations/utils/rosterRows'

dayjs.extend(utc)

export type { InvitationDeliveryStatus }

interface Props {
  isAccepted?: boolean
  isInvited?: boolean
  deliveryStatus?: InvitationDeliveryStatus | null
  deliveryAt?: string | null
}

const tagStyles = css`
  min-width: 90px;
  text-align: center;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  gap: 6px;
  white-space: nowrap;
`

interface Appearance {
  label: string
  tooltip: string
  background: string
  border: string
  icon: React.ReactNode
}

const formatDate = (value?: string | null): string | null => {
  if (!value) {
    return null
  }
  const parsed = dayjs.utc(value)
  return parsed.isValid() ? parsed.local().format('MMM D, YYYY') : null
}

/**
 * Resolve the tag's appearance.
 *
 * Acceptance outranks everything: once someone is on the roster, an email that
 * bounced on an earlier attempt is history, not a problem to flag.
 *
 * Red is reserved for the two states where doing nothing guarantees the person
 * never joins — a wrong address, and a spam complaint. Everything else is
 * informational, so a busy admin scanning the column sees only what needs them.
 */
const resolveAppearance = (
  isAccepted?: boolean,
  isInvited?: boolean,
  deliveryStatus?: InvitationDeliveryStatus | null,
  deliveryAt?: string | null
): Appearance | null => {
  const on = formatDate(deliveryAt)

  if (isAccepted) {
    return {
      label: 'Accepted',
      tooltip: 'This invitation has been accepted.',
      background: 'rgba(57, 187, 85, 0.20)',
      border: colors.SUCCESS_200,
      icon: <CheckCircleOutlined />,
    }
  }

  switch (deliveryStatus) {
    case 'bounced':
      return {
        label: 'Bounced',
        tooltip: on
          ? `The email couldn't be delivered on ${on}. Update the address and send again.`
          : "The email couldn't be delivered. Update the address and send again.",
        background: 'rgba(223, 66, 66, 0.20)',
        border: colors.ERROR_200,
        icon: <CloseCircleOutlined />,
      }

    case 'complained':
      return {
        label: 'Marked spam',
        tooltip: on
          ? `Reported as spam on ${on}. Please don't send this person more reminders.`
          : "Reported as spam. Please don't send this person more reminders.",
        background: 'rgba(223, 66, 66, 0.20)',
        border: colors.ERROR_200,
        icon: <CloseCircleOutlined />,
      }

    case 'unsubscribed':
      return {
        label: 'Opted out',
        tooltip: on
          ? `Asked to stop receiving invitation reminders on ${on}. Their invitation is still valid and they can accept it any time.`
          : 'Asked to stop receiving invitation reminders. Their invitation is still valid and they can accept it any time.',
        background: 'rgba(255, 204, 49, 0.20)',
        border: colors.WARNING_200,
        icon: <StopOutlined />,
      }

    case 'no_email':
      return {
        label: 'No email',
        tooltip:
          'There is no email address on file, so no invitation can be sent.',
        background: colors.NEUTRAL_100,
        border: colors.NEUTRAL_200,
        icon: <InfoCircleOutlined />,
      }

    case 'not_sent':
      return {
        label: 'Not sent',
        tooltip: 'An invitation exists but no email has gone out yet.',
        background: colors.NEUTRAL_100,
        border: colors.NEUTRAL_200,
        icon: <ClockCircleOutlined />,
      }

    case 'delivered':
      return {
        label: 'Invited',
        tooltip: on ? `Delivered on ${on}.` : 'Delivered.',
        background: 'rgba(90, 166, 255, 0.20)',
        border: colors.INFO_200,
        icon: <MailOutlined />,
      }

    case 'sent':
      return {
        label: 'Invited',
        tooltip: on
          ? `Sent on ${on}. Delivery hasn't been confirmed yet.`
          : "Sent. Delivery hasn't been confirmed yet.",
        background: 'rgba(90, 166, 255, 0.20)',
        border: colors.INFO_200,
        icon: <ClockCircleOutlined />,
      }

    default:
      break
  }

  // No delivery information. Older invitations pre-date delivery tracking, so
  // fall back to the flag the roster has always had rather than showing nothing.
  if (isInvited) {
    return {
      label: 'Invited',
      tooltip: 'An invitation has been sent.',
      background: 'rgba(90, 166, 255, 0.20)',
      border: colors.INFO_200,
      icon: <MailOutlined />,
    }
  }

  return null
}

/**
 * InvitationStatusTag shows where someone is in the invitation flow, including
 * whether the email actually reached them.
 *
 * Before delivery tracking, "Invited" meant only that an email had been queued —
 * a message sitting in someone's inbox and one that bounced off a typo months
 * ago looked identical. This surfaces that difference.
 *
 * Renders nothing when there is no invitation, matching the previous behaviour
 * of the Invite column.
 */
const InvitationStatusTag = ({
  isAccepted,
  isInvited,
  deliveryStatus,
  deliveryAt,
}: Props) => {
  const appearance = resolveAppearance(
    isAccepted,
    isInvited,
    deliveryStatus,
    deliveryAt
  )

  if (!appearance) {
    return null
  }

  return (
    <Tooltip title={appearance.tooltip} placement='left'>
      <div
        css={[
          tagStyles,
          css`
            background-color: ${appearance.background};
            border: 1px solid ${appearance.border};
          `,
        ]}
      >
        {appearance.icon}
        <Text
          variant='paragraphXSmall'
          weight='medium'
          color={colors.NEUTRAL_0}
        >
          {appearance.label}
        </Text>
      </div>
    </Tooltip>
  )
}

export default memo(InvitationStatusTag)
